import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ListItem from '../../../../../reusableComponents/baseListItems/baseList13';
import Button from '../../../../../reusableComponents/buttons/button12';
import useEnrolledCourses from '../../../../../../collabs/dashboard+learnings/enrolledCourses/useEnrolledCourses';
import { useAuth } from '../../../../../../context/AuthContext';

const EnrolledCoursesList = () => {
    const navigate = useNavigate();
    const { refreshTokenSilently, isAuthenticated } = useAuth();
    const [isRefreshing, setIsRefreshing] = useState(false);
    
    // Use the shared hook - showing all courses with descriptions
    const { formattedCourses, isLoading, error, refetch } = useEnrolledCourses({ 
        showDescription: true 
    });

    // Handle course navigation
    const handleResumeCourse = (courseId, batchId, courseTitle) => {
        console.log(`Resuming course: ${courseTitle}`);
        console.log(`Course ID: ${courseId}, Batch ID: ${batchId}`);
        navigate(`/learning/courses/course?courseId=${courseId}&batchId=${batchId}`);
    };
    
    // Handle retry with token refresh
    const handleRetry = async () => {
        setIsRefreshing(true);
        try {
            // Try to refresh token first if authentication issue
            const refreshed = await refreshTokenSilently();
            
            // Refetch regardless of token refresh result
            refetch();
            
            // If token refresh failed and we're not authenticated, navigate to login
            if (!refreshed && !isAuthenticated()) {
                setTimeout(() => {
                    navigate('/login', { replace: true });
                }, 500);
            }
        } catch (error) {
            console.error("Error during retry:", error);
        } finally {
            setIsRefreshing(false);
        }
    };
    
    // Show loading state with skeleton UI
    if (isLoading || isRefreshing) {
        return (
            <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3].map((i) => (
                    <div key={i} className="bg-gray-100 animate-pulse h-24 rounded-lg"></div>
                ))}
            </div>
        );
    }
    
    // Show error state with retry option
    if (error) {
        return (
            <div className="text-red-500 text-center p-4 bg-red-50 rounded-lg">
                <p className="mb-2">{error}</p>
                <button 
                    onClick={handleRetry} 
                    className="text-blue-500 underline px-4 py-2 bg-white rounded-md shadow-sm"
                    disabled={isRefreshing}
                >
                    {isRefreshing ? "Attempting to reconnect..." : "Retry"}
                </button>
            </div>
        );
    }
    
    // Show empty state if no courses
    if (!formattedCourses || formattedCourses.length === 0) {
        return (
            <div className="text-center py-6 text-gray-500">
                <p className="mb-2">You haven't enrolled in any courses yet.</p>
                <a href="/learning/courses" className="text-blue-500 underline">
                    Browse available courses
                </a>
            </div>
        );
    }
    
    // Render the courses - now using the pre-formatted data from the hook
    return (
        <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {formattedCourses.map((course, index) => (
                <div key={course.id || index} className="w-full">
                    <ListItem
                        image={course.image}
                        label={course.label}
                        showLabel={course.showLabel}
                        title={course.title}
                        showDescription={course.showDescription}
                        description={course.description}
                        width="w-full"
                    >
                        <Button
                            text="Resume"
                            state="Hover"
                            type="Secondary"
                            variant="Default"
                            onClick={() => handleResumeCourse(course.courseId, course.batchId, course.title)}
                            className="text-xs sm:text-sm"
                        />
                    </ListItem>
                </div>
            ))}
        </div>
    );
};

export default EnrolledCoursesList;